/**
 * Badge Style Utilities
 * Helper functions for generating badge styles based on status, category, difficulty, etc.
 */

import { COLORS, BORDER_RADIUS, FONT_SIZE, FONT_WEIGHT } from '../constants/theme';

/**
 * Base badge style
 */
const baseBadgeStyle = {
  display: 'inline-block',
  padding: '4px 12px',
  borderRadius: BORDER_RADIUS.lg,
  fontSize: FONT_SIZE.sm,
  fontWeight: FONT_WEIGHT.medium,
  textTransform: 'uppercase',
};

/**
 * Get status badge style (completed/pending)
 */
export const getStatusBadgeStyle = (completed) => {
  const colors = completed ? COLORS.badge.completed : COLORS.badge.pending;

  return {
    ...baseBadgeStyle,
    backgroundColor: colors.bg,
    color: colors.text,
  };
};

/**
 * Get category badge style (hitting, catching, fielding, pitching)
 */
export const getCategoryBadgeStyle = (category) => {
  const colors = COLORS.badge[category] || { bg: COLORS.gray[100], text: COLORS.gray[600] };

  return {
    ...baseBadgeStyle,
    backgroundColor: colors.bg,
    color: colors.text,
  };
};

/**
 * Get difficulty badge style (beginner, intermediate, advanced)
 */
export const getDifficultyBadgeStyle = (difficulty) => {
  const colors = COLORS.badge[difficulty] || { bg: COLORS.gray[100], text: COLORS.gray[600] };

  return {
    ...baseBadgeStyle,
    backgroundColor: colors.bg,
    color: colors.text,
  };
};

/**
 * Get role badge style (player, parent, coach, admin, super_admin)
 */
export const getRoleBadgeStyle = (role) => {
  const roleColors = {
    admin: { bg: '#e3f2fd', text: '#1976d2' },
    coach: { bg: '#f3e5f5', text: '#7b1fa2' },
    player: { bg: '#e8f5e9', text: '#388e3c' },
    parent: { bg: '#fff3e0', text: '#f57c00' },
    super_admin: { bg: '#f3e5f5', text: '#4a148c' },
  };

  const colors = roleColors[role] || roleColors.player;

  return {
    ...baseBadgeStyle,
    backgroundColor: colors.bg,
    color: colors.text,
  };
};

/**
 * Get progress badge style based on percentage
 * @param {number} percentage - Progress percentage (0-100)
 */
export const getProgressBadgeStyle = (percentage) => {
  let colors;

  if (percentage >= 80) {
    colors = { bg: COLORS.badge.completed.bg, text: COLORS.badge.completed.text };
  } else if (percentage >= 50) {
    colors = { bg: '#fff3e0', text: '#f57c00' };
  } else if (percentage > 0) {
    colors = { bg: '#fff9e6', text: '#ffa726' };
  } else {
    colors = { bg: COLORS.gray[100], text: COLORS.gray[600] };
  }

  return {
    ...baseBadgeStyle,
    backgroundColor: colors.bg,
    color: colors.text,
  };
};
